import React, { useRef, useState, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
    ImageryLayerFeatureInfo,
    Cartesian3,
    Cartographic,
    Math as CesiumMath,
    Cesium3DTileFeature,
} from 'cesium';
import { CloseOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import InfoTool from './InfoTool';
import classnames from 'classnames';
import {
    point as TurfPoint,
    lineString as TurfLineString,
    nearestPointOnLine as TurfNearestPointOnLine,
    midpoint as TurfMidpoint,
    polygon as TurfPolygon,
    center as TurfCenter,
    centerOfMass as TurfCenterOfMass,
    featureCollection as TurfFeatureCollection,
} from '@turf/turf';
import MarkerGeo from '../marker/Marker';
import PolygonGeo from '../polygon/Polygon';
import PolylineGeo from '../polyline/Polyline';
import { getImgUrl } from '../tools/layer-control';
import { transformGeometry } from '../feature-layer/createFeatureCollection';
import Popup from '../popup';

const ClusterTitle = props => {
    const { layer } = props;
    let { name } = layer;

    const imgUrl = getImgUrl(layer);

    return (
        <div className={classnames('cs-map-popup-cluster-title')}>
            <img src={imgUrl} alt="" className={classnames('cs-map-popup-cluster-title-img')} />
            <span className={classnames('cs-map-popup-cluster-title-text')}>{name}</span>
        </div>
    );
};

// 根据图片地址获取图片高度
const getImageHeightBySrc = src => {
    return new Promise(resolve => {
        // js从base64数据中获取宽高
        let image = new Image();
        image.src = src;
        image.onload = () => {
            resolve(image.height);
        };
    });
};

ClusterTitle.propTypes = {
    layer: PropTypes.object,
};

const Cluster = props => {
    const { layer, features, onFeatureClick, onFitView } = props;
    let { labelField } = layer;

    return (
        <div className={classnames('cs-map-popup-cluster')}>
            <div className={classnames('cs-map-popup-cluster-header')}>
                共 {features.length} 个点位
                <a onClick={onFitView}>缩放至</a>
            </div>
            <Table
                pagination={false}
                size="small"
                dataSource={features}
                scroll={{ y: 120 }}
                columns={[
                    {
                        title: '名称',
                        dataIndex: '名称',
                        width: 60,
                        render: (text, record) => {
                            return (
                                <a onClick={() => onFeatureClick(record)}>
                                    {record.values_[labelField]}
                                </a>
                            );
                        },
                    },
                    {
                        title: '操作',
                        dataIndex: '操作',
                        width: 30,
                        render: (text, record) => {
                            return <a onClick={() => onFeatureClick(record)}>定位</a>;
                        },
                    },
                ]}
            />
        </div>
    );
};
Cluster.propTypes = {
    layer: PropTypes.object,
    features: PropTypes.array,
    onFeatureClick: PropTypes.func,
    onFitView: PropTypes.func,
};

export const handelGeometry = (entity) => {
    let geometry = entity?.data?.geometry;
    let type = geometry?.type;
    let coordinates = geometry?.coordinates;

    let midpoint = entity?.data; // 默认点
    let position = null;
    let offset = {
        x: 0,
        y: 20,
    };

    if (['MultiLineString', 'LineString'].includes(type) && coordinates?.length) {
        if (coordinates.length >= 3) {
            const features = TurfFeatureCollection(coordinates.map(lngLatArr => TurfPoint(lngLatArr)));
            const center = TurfCenter(features);
            const line = TurfLineString(coordinates);
            midpoint = TurfNearestPointOnLine(line, center, {units: 'miles'});
        } else if (coordinates.length > 1) {
            midpoint = TurfMidpoint(...coordinates.map(lngLatArr => TurfPoint(lngLatArr)));
        } else {
            let lngLatArr = coordinates[0];
            midpoint = TurfMidpoint(TurfPoint(lngLatArr[0]), TurfPoint(lngLatArr[1]))
        }
    } else if (['Polygon', 'MultiPolygon'].includes(type)) {
        // 兼容polygon首尾坐标不一致情况
        coordinates = coordinates?.map(item => {
            if (
                item[0][0] !== item[item.length - 1][0] ||
                item[0][1] !== item[item.length - 1][1]
            ) {
                return [...item, item[0]];
            }
            return item;
        });
        // 面
        // 注意：polygon首尾坐标要一致
        let polygon = TurfPolygon(coordinates);
        midpoint = TurfCenterOfMass(polygon);
    } else if (['Point'].includes(type)) {
        offset = {
            x: 5,
            y: 20,
        };
    }
    if (midpoint?.geometry?.coordinates) {
        position = Cartesian3.fromDegrees(...midpoint.geometry.coordinates);
    }

    return {
        position,
        geometryType: type,
        _offset: offset,
    };
};

const MapPopup = forwardRef((props, ref) => {
    const { map } = props;

    const [content, setContent] = useState(null);
    const [title, setTitle] = useState(null);
    const popupRef = useRef();

    const featureRef = useRef();
    const infoToolRef = useRef(new InfoTool({ map }));
    const viewRef = useRef();
    const highlightLayerRef = useRef();

    useImperativeHandle(ref, () => {
        return {
            showTemplate,
            hideTemplate,
        };
    });

    const showTemplate = async (feature, highlightLayer, options) => {
        setContent(null);

        if (!feature) {
            hideTemplate();
            return;
        }

        // 判断是否为聚合点击
        if (feature && feature.length >= 2) {
            const points = [];
            const features = feature.map(f => {
                let properties = map.getProperties(f);

                points.push(TurfPoint(f.geometry));
                return {
                    feature: f,
                    geometryInfo: properties?.geometryInfo || {
                        type: 'point',
                        lngLats: f.geometry.join(','),
                        coordType: 'wgs84',
                    },
                    values_: properties,
                    properties: properties,
                };
            });

            let layer = map.getLayerByFeature(feature[0]);
            infoToolRef.current.add({
                position: map._clickObject.position,
                content: '',
                element: popupRef.current?.wrapDom,
                inputFeature: feature,
                type: '',
                offset: {
                    x: 0,
                    y: 20,
                },
            });

            setTitle(<ClusterTitle layer={layer} />);

            // 判断是否有自定义聚合列表模版
            let Template =
                layer?._options?.cluster?.template || layer?.cluster?.template || Cluster;
            setContent(
                <Template
                    layer={layer}
                    features={features}
                    onFitView={() => {
                        map.fitView({ data: features });
                        hideTemplate(null, map.autoView);
                    }}
                    onFeatureClick={f => {
                        showTemplate(f.feature, highlightLayer);
                    }}
                />,
            );
            return;
        }

        let layer = map.getLayerByFeature(feature);
        if (layer?.cancelPopup || feature?.cancelPopup) {
            return;
        }
        map.layers.highlightFeature = feature;

        let properties = map.getProperties(feature);
        feature.values_ = properties;
        featureRef.current = feature;
        let position = feature?._position?._value;
        // 偏移量
        let offset = {
            x: 5,
            y: 76,
        };

        let height = map.fitViewHeight || 5000;
        if (feature instanceof MarkerGeo || feature?._billboard) {
            if (feature._billboard.scale?._value) {
                offset.y = offset.y * feature._billboard.scale._value;
            }

            let src = feature._billboard?._image?._value || '';
            if (src) {
                let height = await getImageHeightBySrc(src);

                if (height && typeof height === 'number') {
                    if (feature._billboard.scale?._value) {
                        height = height * feature._billboard.scale._value;
                    }

                    offset.y = height + 10;
                }
            }
        } else if (feature instanceof PolygonGeo || feature?._polygon) {
            let coordinates = [
                (feature?._polygon?._hierarchy?._value?.positions || []).map(v => {
                    const cartographic = Cartographic.fromCartesian(v); // 经纬高对象
                    const lng = CesiumMath.toDegrees(cartographic.longitude);
                    const lat = CesiumMath.toDegrees(cartographic.latitude);
                    return [lng, lat];
                }),
            ];
            let { position: _position, _offset } = handelGeometry({
                data: { geometry: { type: 'Polygon', coordinates } },
            });
            position = _position;
            offset = _offset;
            height = null;
        } else if (feature instanceof PolylineGeo || feature?._polyline) {
            let coordinates = (feature?._polyline?._positions?._value || []).map(v => {
                const cartographic = Cartographic.fromCartesian(v); // 经纬高对象
                const lng = CesiumMath.toDegrees(cartographic.longitude);
                const lat = CesiumMath.toDegrees(cartographic.latitude);
                return [lng, lat];
            });
            let { position: _position, _offset } = handelGeometry({
                data: { geometry: { type: 'LineString', coordinates } },
            });
            position = _position;
            offset = _offset;
            height = null;
        }

        if (feature instanceof Cesium3DTileFeature) {
            offset = {
                x: 0,
                y: 20,
            };
            position = map._clickObject.position;
            // position = Cartesian3.fromDegrees();
        } else if (feature.primitive) {
            if (properties.geometryInfo) {
                offset = {
                    x: 0,
                    y: 20,
                };
                const geometry = transformGeometry(properties.geometryInfo);
                const [point1, point2] = geometry.coordinates[0];
                const midpoint = TurfMidpoint(point1, point2);
                if (midpoint?.geometry?.coordinates) {
                    position = Cartesian3.fromDegrees(...midpoint.geometry.coordinates);
                }
            } else {
                offset = {
                    x: 0,
                    y: 20,
                };
                position = map._clickObject.position;
            }
        }

        // 影像图层的数据
        if (feature instanceof ImageryLayerFeatureInfo || feature?.data?.geometry?.coordinates) {
            let { position: _position, _offset } = handelGeometry(feature);
            position = _position;
            offset = _offset;
        }

        if (layer?.template) {
            let template = layer.template;
            let labelField = layer.labelField;

            setTitle(labelField && properties[labelField]);
            setContent(feature && template(feature));

            infoToolRef.current.add({
                position: position,
                content: '',
                element: popupRef.current?.wrapDom,
                inputFeature: feature,
                type: '',
                offset,
            });

            map.popupRef = feature;
        }

        const currentCameraHeight = map.camera.positionCartographic.height;
        const viewThreshold = currentCameraHeight < height;
        const viewHeight = viewThreshold ? currentCameraHeight : height;
        if (!viewRef.current && map.autoView) {
            getView();
        }

        if (viewThreshold) {
            viewRef.current = null;
        }

        highlightLayer = highlightLayer || map.highlightLayer;
        highlightLayerRef.current = highlightLayer;
        highlightLayer.addHighlightFeature(feature);
        if (highlightLayer?.layer && map.autoView) {
            // 影像图层的数据
            if (
                feature instanceof ImageryLayerFeatureInfo &&
                map.viewMode === '2D' &&
                feature?.data?.geometry?.type !== 'Point'
            ) {
                map.fitView({
                    geoJson: { features: [feature.data] },
                });
            } else {
                map.fitView({
                    entity: highlightLayer.layer,
                    height: viewHeight,
                });
            }
        }
    };

    const hideTemplate = _layer => {
        if (_layer) {
            let layer = typeof _layer === 'string' ? map.getLayerByKey(_layer) : _layer;

            if (layer && layer.key && map.popupRef) {
                let _highlightLayer = map.getLayerByFeature(map.popupRef);
                if (_highlightLayer.key === layer.key) {
                    onClose();
                }
            }
        } else {
            onClose();
        }
    };

    // 判断点位异常-点位异常展示红色
    function judgeAbnormal() {
        const { status } = map.getProperties(featureRef.current) || {};

        return status && [1, 2, 4].includes(status);
    }

    // 关闭气泡
    const onClose = () => {
        map.onCloseTemplateBefore && map.onCloseTemplateBefore(featureRef.current);
        map.popupRef = null;
        featureRef.current = null;
        infoToolRef?.current?.remove();

        map.layers.highlightFeature = null;

        if (map.entities.getById('highlight-1')) {
            map.entities.remove(map.entities.getById('highlight-1'));
        }

        // 清除高亮
        highlightLayerRef.current?.clearHighlight?.();
        // highlightLayerRef.current.clearFeatures();

        // map.fitView();

        if (map.autoView && map.closePopupAutoView) {
            setView();
        }
        setContent(null);
    };

    const getView = () => {
        //获取当前视角的 pitch
        let pitch = map.camera.pitch;

        //获取当前视角的 heading
        let heading = map.camera.heading;

        //获取当前视角的 position（位置）
        let position = map.camera.position;

        viewRef.current = {
            x: position.x,
            y: position.y,
            z: position.z,
            pitch: pitch,
            heading: heading,
            viewMode: map.viewMode,
        };
    };

    const setView = () => {
        if (viewRef.current && viewRef.current.viewMode === map.viewMode) {
            map.camera.flyTo({
                //定位到范围中心点
                destination: {
                    x: viewRef.current.x,
                    y: viewRef.current.y,
                    z: viewRef.current.z,
                },
                orientation: {
                    heading: viewRef.current.heading, //左右方向
                    pitch: viewRef.current.pitch, //上下方向
                    roll: 0.0,
                },
                duration: 2,
            });

            viewRef.current = null;
        }
    };

    const theme = judgeAbnormal() ? 'red' : '';
    return (
        <Popup ref={popupRef} title={title} onClose={onClose} theme={theme}>
            {content}
        </Popup>
    );
});

MapPopup.propTypes = {
    map: PropTypes.object,
    feature: PropTypes.object,
    layer: PropTypes.object,
    highlightLayer: PropTypes.object,
    onClose: PropTypes.func,
};

export default MapPopup;
